import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Badge, ButtonBase, Collapse } from '@material-ui/core';
import { ExpandMore, FilterList } from '@material-ui/icons';

/**
 * Layout pieces shared by the character and user pages. On desktop the page is a fixed frame with tabs on the
 * side and each tab scrolling inside it; on phones the page scrolls normally: profile, sticky tabs, filters, tab.
 */
export const useProfileLayoutStyles = makeStyles((theme) => ({
  mobilePage: {
    padding: theme.spacing(1),
  },
  // stays under the app bar (56px on phones, 48px in landscape) while the page scrolls
  mobileTabs: {
    position: 'sticky',
    top: 56,
    '@media (orientation: landscape)': {
      top: 48,
    },
    zIndex: 3,
    margin: theme.spacing(0, -1, 1),
    padding: theme.spacing(0.75, 1),
    background: theme.palette.background.default,
    '& .MuiTab-root': {
      minHeight: 40,
      padding: theme.spacing(0.5, 1.5),
    },
  },
  mobileContent: {
    '& .MuiPaper-root': {
      background: 'rgba(20, 20, 20, 0.92)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
      boxShadow: 'none',
    },
  },
  // list tabs scroll themselves: as tall as the screen below the app bar and the sticky tabs
  mobileFill: {
    height: 'calc(100vh - 56px - 56px)',
    '@media (orientation: landscape)': {
      height: 'calc(100vh - 48px - 56px)',
    },
    minHeight: 320,
  },
  filtersBar: {
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.12)',
    background: 'rgba(20, 20, 20, 0.92)',
    overflow: 'hidden',
  },
  filtersToggle: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: `${theme.spacing(1.25)}px`,
    padding: theme.spacing(1.25, 1.5),
    textAlign: 'left',
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  chevron: {
    transition: 'transform 0.2s',
    opacity: 0.7,
  },
  filtersBody: {
    padding: theme.spacing(0, 1, 1),
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
  /** the content fills the panel and scrolls itself (virtualized lists) */
  fill?: boolean;
  /** phone layout: the page scrolls, so only list tabs get a height */
  mobile?: boolean;
}

export const ProfileTabPanel: React.FC<TabPanelProps> = ({children, value, index, fill, mobile}) => {
  const classes = useProfileLayoutStyles();
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={mobile && fill ? classes.mobileFill : undefined}
      style={mobile ? undefined : {flex: '1 1 auto', minHeight: 0, overflow: fill ? 'hidden' : 'auto'}}
    >
      {value === index && children}
    </div>
  );
}

interface MobileFiltersProps {
  /** how many filters differ from the defaults */
  active: number;
  open: boolean;
  onToggle: () => void;
}

/** On phones the filters fold away behind a bar showing how many are set. */
export const MobileFilters: React.FC<MobileFiltersProps> = ({active, open, onToggle, children}) => {
  const classes = useProfileLayoutStyles();
  return (
    <div className={classes.filtersBar}>
      <ButtonBase className={classes.filtersToggle} onClick={onToggle}>
        <Badge badgeContent={active} color="primary"><FilterList fontSize="small" /></Badge>
        <span>{active ? `Filters · ${active} set` : 'Filters'}</span>
        <ExpandMore className={classes.chevron} style={{transform: open ? 'rotate(180deg)' : undefined}} />
      </ButtonBase>
      <Collapse in={open}>
        <div className={classes.filtersBody}>{children}</div>
      </Collapse>
    </div>
  );
}
